import {
  AdditiveBlending,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  type Object3D,
  OctahedronGeometry,
  PlaneGeometry,
  type Scene,
  ShaderMaterial,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  Vector3,
} from 'three';
import type { Particles } from './particles';

/**
 * Łuk płaski na ziemi: uv.x = położenie wzdłuż łuku (0..1), uv.y = odległość od środka (0..1).
 * Budowany wokół kąta 0 (oś +X); kierunek ustawia się obrotem wokół Y.
 */
function arcGeometry(arc: number, inner: number, segments = 48): BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const u = i / segments;
    const a = -arc / 2 + u * arc;
    for (const [r, v] of [
      [inner, 0],
      [1, 1],
    ] as const) {
      pos.push(Math.cos(a) * r, 0, Math.sin(a) * r);
      uv.push(u, v);
    }
    if (i < segments) {
      const k = i * 2;
      idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('uv', new BufferAttribute(new Float32Array(uv), 2));
  g.setIndex(idx);
  return g;
}

/**
 * Smuga cięcia: jasna, prawie biała krawędź zewnętrzna przechodząca w kolor czaru,
 * smugi „włókien” wzdłuż łuku, przesuwa się i gaśnie.
 */
function slashMaterial(color: Color): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: { color: { value: color }, progress: { value: 0 }, fade: { value: 1 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform vec3 color; uniform float progress; uniform float fade;
      varying vec2 vUv;
      void main() {
        // Przycięcie: przy wygładzaniu krawędzi uv bywa odrobinę poza [0, 1] (pow z ujemnej = NaN).
        vec2 uv = clamp(vUv, 0.0, 1.0);
        float edge = pow(uv.y, 2.6);
        float streaks = 0.75 + 0.25 * sin(uv.y * 60.0 + uv.x * 6.0);
        float tail = smoothstep(progress - 0.6, progress, uv.x) * step(uv.x, progress);
        float ends = smoothstep(0.0, 0.08, uv.x) * (1.0 - smoothstep(0.9, 1.0, uv.x));
        float a = clamp(edge * tail * ends * fade * streaks, 0.0, 1.0);
        vec3 c = mix(color, vec3(1.0), smoothstep(0.85, 1.0, uv.y) * 0.8);
        gl_FragColor = vec4(c * (1.2 + 1.8 * edge), a);
      }`,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
  });
}

interface Effect {
  update(dt: number): boolean; // false = do usunięcia
}

let glowTexture: CanvasTexture | null = null;
/** Miękka, okrągła poświata (do sprite'ów). */
export function glow(): CanvasTexture {
  if (glowTexture) return glowTexture;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.18, 'rgba(255,255,255,0.75)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.22)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  glowTexture = new CanvasTexture(c);
  return glowTexture;
}

let streakTexture: CanvasTexture | null = null;
/** Smuga za pociskiem: jasna z przodu, zanikająca ku tyłowi, miękka na bokach. */
function streak(): CanvasTexture {
  if (streakTexture) return streakTexture;
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 32;
  const ctx = c.getContext('2d')!;
  const along = ctx.createLinearGradient(0, 0, 128, 0);
  along.addColorStop(0, 'rgba(255,255,255,0)');
  along.addColorStop(0.85, 'rgba(255,255,255,0.9)');
  along.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = along;
  ctx.fillRect(0, 0, 128, 32);
  ctx.globalCompositeOperation = 'destination-in';
  const across = ctx.createLinearGradient(0, 0, 0, 32);
  across.addColorStop(0, 'rgba(0,0,0,0)');
  across.addColorStop(0.5, 'rgba(0,0,0,1)');
  across.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = across;
  ctx.fillRect(0, 0, 128, 32);
  streakTexture = new CanvasTexture(c);
  return streakTexture;
}

/** Efekty umiejętności w świecie gry: cięcia, wiry, fale ognia, błyski trafień. */
export class FxLayer {
  private effects: Effect[] = [];
  private readonly arcs = new Map<string, BufferGeometry>();
  /** Wstrząs kamery do odczytania przez scenę. */
  shake = 0;

  constructor(
    private readonly scene: Scene,
    private readonly sparks: Particles,
    private readonly smoke: Particles,
  ) {}

  private arc(arc: number, inner: number, segments = 48): BufferGeometry {
    const key = `${arc.toFixed(3)}|${inner}|${segments}`;
    let g = this.arcs.get(key);
    if (!g) {
      g = arcGeometry(arc, inner, segments);
      this.arcs.set(key, g);
    }
    return g;
  }

  /** Krótki błysk światła (sprite), np. przy trafieniu albo wystrzale. */
  flash(at: Vector3, color: string, size: number, duration = 0.16): void {
    const s = new Sprite(new SpriteMaterial({ map: glow(), color: new Color(color), blending: AdditiveBlending, depthWrite: false, toneMapped: false }));
    s.position.copy(at);
    this.scene.add(s);
    let t = 0;
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        s.scale.setScalar(size * (0.6 + k * 0.8));
        s.material.opacity = 1 - k;
        if (k >= 1) {
          s.removeFromParent();
          s.material.dispose();
          return false;
        }
        return true;
      },
    });
  }

  /** Smuga łukowa: cięcie (`heavy` = cios z góry: krótszy, mocniejszy, z pyłem i wstrząsem). */
  slash(center: Vector3, angle: number, radius: number, arc: number, color: string, heavy = false): void {
    const c = new Color(color);
    const mat = slashMaterial(c);
    const mesh = new Mesh(this.arc(arc, heavy ? 0.15 : 0.5), mat);
    mesh.position.set(center.x, heavy ? 0.25 : 1.0, center.z);
    mesh.rotation.y = -angle;
    mesh.scale.setScalar(radius);
    this.scene.add(mesh);
    const duration = heavy ? 0.32 : 0.26;
    let t = 0;
    const p = new Vector3();
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        const progress = Math.min(1.5, k * 2.2);
        mat.uniforms.progress!.value = progress;
        mat.uniforms.fade!.value = 1 - Math.max(0, k - 0.45) / 0.55;
        // Iskry odpryskujące z czoła smugi.
        if (progress <= 1) {
          const a = angle - arc / 2 + arc * progress;
          p.set(center.x + Math.cos(a) * radius * 0.95, mesh.position.y, center.z + Math.sin(a) * radius * 0.95);
          this.sparks.emit(p, { color, count: 2, speed: 2.5, life: 0.35, size: 0.16, grow: 0.3, hot: true, gravity: 4 });
        }
        if (k >= 1) {
          mesh.removeFromParent();
          mat.dispose();
          return false;
        }
        return true;
      },
    });
    if (heavy) {
      const tip = new Vector3(center.x + Math.cos(angle) * radius * 0.7, 0.2, center.z + Math.sin(angle) * radius * 0.7);
      this.smoke.emit(tip, { color: '#8a7a5c', count: 22, speed: 3.5, life: 0.9, up: 1.2, gravity: 2, spread: 0.8, grow: 2.5, drag: 2.5 });
      this.sparks.emit(tip, { color, count: 26, speed: 7, life: 0.45, up: 2, gravity: 9, size: 0.2, grow: 0.3, hot: true });
      this.flash(tip.setY(0.6), color, 3.2, 0.22);
      this.shake = Math.max(this.shake, 0.25);
    }
  }

  /** Wir: dwie smugi okrążające postać i kurz unoszący się z ziemi. */
  spin(center: Vector3, radius: number, color: string, follow: Object3D): void {
    const c = new Color(color);
    const mats = [slashMaterial(c), slashMaterial(c)];
    const group = new Group();
    mats.forEach((m, i) => {
      const mesh = new Mesh(this.arc(Math.PI * 1.2, 0.45), m);
      mesh.rotation.y = i * Math.PI;
      group.add(mesh);
    });
    group.position.set(center.x, 0.9, center.z);
    group.scale.setScalar(radius);
    this.scene.add(group);
    const duration = 0.62;
    let t = 0;
    const p = new Vector3();
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        group.position.set(follow.position.x, 0.9, follow.position.z);
        // Smuga idzie za bronią: start wolny, szczyt w połowie obrotu, wyhamowanie.
        const ease = k < 1 ? 0.5 - Math.cos(k * Math.PI) / 2 : 1;
        group.rotation.y = follow.rotation.y - ease * Math.PI * 2;
        for (const m of mats) {
          m.uniforms.progress!.value = 1;
          // Smuga obrotu przygaszona — jasne kolory (błękit, złoto) inaczej wybielają się w poświacie.
          m.uniforms.fade!.value = Math.sin(Math.min(1, k) * Math.PI) * 0.55;
        }
        const a = Math.random() * Math.PI * 2;
        p.set(follow.position.x + Math.cos(a) * radius * 0.8, 0.15, follow.position.z + Math.sin(a) * radius * 0.8);
        this.smoke.emit(p, { color: '#9c8a66', count: 1, speed: 0.6, life: 0.7, up: 0.8, size: 0.7, grow: 2, drag: 2 });
        if (k >= 1) {
          group.removeFromParent();
          mats.forEach((m) => m.dispose());
          return false;
        }
        return true;
      },
    });
    this.shake = Math.max(this.shake, 0.08);
  }

  /** Fala ognia: rozchodzący się żarzący pierścień, płomienie na krawędzi i dym. */
  flame(center: Vector3, radius: number, color: string): void {
    const mat = slashMaterial(new Color(color));
    const ring = new Mesh(this.arc(Math.PI * 2, 0.75, 72), mat);
    ring.position.set(center.x, 0.08, center.z);
    this.scene.add(ring);
    this.flash(new Vector3(center.x, 0.8, center.z), '#ffb36b', radius * 1.2, 0.3);
    const duration = 0.5;
    let t = 0;
    const p = new Vector3();
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        const r = radius * (0.2 + 0.8 * Math.min(1, k * 1.25));
        ring.scale.setScalar(r);
        mat.uniforms.progress!.value = 1.5;
        mat.uniforms.fade!.value = 1 - k;
        for (let i = 0; i < 10; i++) {
          const a = Math.random() * Math.PI * 2;
          p.set(center.x + Math.cos(a) * r, 0.25, center.z + Math.sin(a) * r);
          this.sparks.emit(p, { color: i % 3 ? color : '#ffd27a', count: 1, speed: 0.5, life: 0.55, up: 2.6, size: 0.45, grow: 0.2, hot: true, drag: 1 });
          if (i === 0) this.smoke.emit(p, { color: '#3a2a20', count: 1, speed: 0.3, life: 1, up: 1.5, size: 0.8, grow: 2.2, drag: 1 });
        }
        if (k >= 1) {
          ring.removeFromParent();
          mat.dispose();
          return false;
        }
        return true;
      },
    });
  }

  /**
   * Zapowiedź szarży: czerwony pas na ziemi w kierunku ataku, wypełnia się przez czas zamachu.
   * Gracz widzi, gdzie wróg pobiegnie, i ma czas uskoczyć.
   */
  telegraphLine(from: Vector3, angle: number, length: number, width: number, duration: number): void {
    const mat = new MeshBasicMaterial({ color: new Color('#ff3a20'), transparent: true, opacity: 0, blending: AdditiveBlending, depthWrite: false, toneMapped: false });
    const plane = new Mesh(new PlaneGeometry(1, 1).translate(0.5, 0, 0).rotateX(-Math.PI / 2), mat);
    plane.position.set(from.x, 0.05, from.z);
    plane.rotation.y = -angle;
    plane.scale.set(length, 1, width);
    this.scene.add(plane);
    let t = 0;
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        plane.scale.x = length * Math.min(1, 0.2 + k);
        mat.opacity = k < 1 ? 0.18 + 0.22 * k + 0.1 * Math.sin(t * 30) : Math.max(0, 0.4 - (k - 1) * 2);
        if (k >= 1.2) {
          plane.removeFromParent();
          plane.geometry.dispose();
          mat.dispose();
          return false;
        }
        return true;
      },
    });
  }

  /** Zapowiedź salwy albo wybuchu: krąg na ziemi, który zaciska się do środka przez czas zamachu. */
  telegraphRing(follow: Object3D, radius: number, duration: number, color: string): void {
    const mat = slashMaterial(new Color(color));
    const ring = new Mesh(this.arc(Math.PI * 2, 0.9, 72), mat);
    this.scene.add(ring);
    let t = 0;
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = Math.min(1, t / duration);
        ring.position.set(follow.position.x, 0.07, follow.position.z);
        ring.scale.setScalar(radius * (1.25 - 0.45 * k));
        mat.uniforms.progress!.value = 1.5;
        mat.uniforms.fade!.value = 0.35 + 0.45 * k;
        if (t >= duration) {
          ring.removeFromParent();
          mat.dispose();
          return false;
        }
        return true;
      },
    });
  }

  /** Wybuch: ognista fala, odłamki i dym. */
  explosion(center: Vector3, radius: number): void {
    this.flame(center, radius, '#ff6a2a');
    this.flash(new Vector3(center.x, 1, center.z), '#ffcf7a', radius * 1.6, 0.25);
    this.sparks.emit(new Vector3(center.x, 0.6, center.z), { color: '#ffb050', count: 30, speed: 7, life: 0.6, up: 3, gravity: 10, size: 0.2, hot: true });
    this.smoke.emit(new Vector3(center.x, 0.5, center.z), { color: '#2e2620', count: 14, speed: 2, life: 1.3, up: 1.2, spread: 0.5, size: 1.1, grow: 2.4, drag: 2 });
    this.shake = Math.max(this.shake, 0.3);
  }

  /** Awans: słup złotego światła nad bohaterem, rozchodzący się krąg i unoszące się iskry. */
  levelUp(follow: Object3D): void {
    const color = new Color('#ffd27a');
    const beamMat = new ShaderMaterial({
      uniforms: { color: { value: color }, fade: { value: 1 } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: /* glsl */ `
        uniform vec3 color; uniform float fade;
        varying vec2 vUv;
        void main() {
          float a = (1.0 - vUv.y) * (1.0 - vUv.y) * fade;
          a *= 0.6 + 0.4 * sin(vUv.x * 40.0 + vUv.y * 8.0);
          gl_FragColor = vec4(color * 2.0, clamp(a, 0.0, 1.0));
        }`,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    });
    const beam = new Mesh(new CylinderGeometry(0.75, 0.95, 9, 32, 1, true).translate(0, 4.5, 0), beamMat);
    const ringMat = slashMaterial(color);
    const ring = new Mesh(this.arc(Math.PI * 2, 0.82, 64), ringMat);
    this.scene.add(beam, ring);
    const duration = 1.3;
    let t = 0;
    const p = new Vector3();
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        beam.position.copy(follow.position);
        ring.position.set(follow.position.x, 0.06, follow.position.z);
        ring.scale.setScalar(0.6 + k * 2.6);
        beamMat.uniforms.fade!.value = Math.sin(Math.min(1, k) * Math.PI);
        ringMat.uniforms.progress!.value = 1.5;
        ringMat.uniforms.fade!.value = 1 - k;
        if (k < 0.8) {
          const a = Math.random() * Math.PI * 2;
          p.set(follow.position.x + Math.cos(a) * 0.8, 0.2, follow.position.z + Math.sin(a) * 0.8);
          this.sparks.emit(p, { color: '#ffd860', count: 2, speed: 0.4, life: 1, up: 3.5, size: 0.2, grow: 0.4, hot: true, drag: 0.5 });
        }
        if (k >= 1) {
          beam.removeFromParent();
          ring.removeFromParent();
          beamMat.dispose();
          ringMat.dispose();
          return false;
        }
        return true;
      },
    });
    this.flash(new Vector3(follow.position.x, 1.2, follow.position.z), '#ffe7a0', 4, 0.35);
  }

  /**
   * Błyskawica łańcuchowa: poszarpane odcinki między kolejnymi celami (jasny rdzeń i kolorowa poświata),
   * błysk przy każdym trafieniu. Gaśnie w ćwierć sekundy.
   */
  lightning(points: Vector3[], color: string): void {
    const c = new Color(color);
    const coreMat = new MeshBasicMaterial({ color: new Color('#ffffff'), transparent: true, blending: AdditiveBlending, depthWrite: false, toneMapped: false });
    const glowMat = new MeshBasicMaterial({ color: c, transparent: true, opacity: 0.7, blending: AdditiveBlending, depthWrite: false, toneMapped: false });
    const group = new Group();
    const a = new Vector3();
    const b = new Vector3();
    for (let i = 0; i < points.length - 1; i++) {
      const from = points[i]!;
      const to = points[i + 1]!;
      const len = from.distanceTo(to);
      const pieces = Math.max(3, Math.round(len / 0.9));
      a.copy(from);
      for (let k = 1; k <= pieces; k++) {
        b.lerpVectors(from, to, k / pieces);
        if (k < pieces) b.add(new Vector3((Math.random() - 0.5) * 0.7, (Math.random() - 0.5) * 0.4, (Math.random() - 0.5) * 0.7));
        const seg = b.clone().sub(a);
        const mid = a.clone().add(b).multiplyScalar(0.5);
        for (const [mat, w] of [[coreMat, 0.06], [glowMat, 0.2]] as const) {
          const m = new Mesh(lightningBox, mat);
          m.position.copy(mid);
          m.scale.set(w, w, seg.length());
          m.lookAt(b);
          group.add(m);
        }
        a.copy(b);
      }
      this.flash(to.clone(), color, 1.6, 0.2);
      this.sparks.emit(to, { color, count: 6, speed: 4, life: 0.3, up: 1, gravity: 6, size: 0.15, hot: true });
    }
    this.scene.add(group);
    const duration = 0.24;
    let t = 0;
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        // Migotanie jak przy prawdziwym wyładowaniu.
        const flicker = 0.6 + 0.4 * Math.sin(t * 90);
        coreMat.opacity = (1 - k) * flicker;
        glowMat.opacity = 0.7 * (1 - k) * flicker;
        if (k >= 1) {
          group.removeFromParent();
          coreMat.dispose();
          glowMat.dispose();
          return false;
        }
        return true;
      },
    });
  }

  /** Tik aury: delikatny pierścień rozchodzący się do krawędzi aury. */
  auraPulse(follow: Object3D, radius: number, color: string): void {
    const mat = slashMaterial(new Color(color));
    const ring = new Mesh(this.arc(Math.PI * 2, 0.88, 72), mat);
    this.scene.add(ring);
    const duration = 0.45;
    let t = 0;
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        ring.position.set(follow.position.x, 0.07, follow.position.z);
        ring.scale.setScalar(radius * (0.7 + 0.3 * k));
        mat.uniforms.progress!.value = 1.5;
        mat.uniforms.fade!.value = 0.45 * (1 - k);
        if (k >= 1) {
          ring.removeFromParent();
          mat.dispose();
          return false;
        }
        return true;
      },
    });
  }

  /** Wybuch pułapki: błysk, krąg w kolorze umiejętności, odłamki i kurz. */
  trapBurst(center: Vector3, radius: number, color: string): void {
    const mat = slashMaterial(new Color(color));
    const ring = new Mesh(this.arc(Math.PI * 2, 0.7, 64), mat);
    ring.position.set(center.x, 0.09, center.z);
    this.scene.add(ring);
    this.flash(new Vector3(center.x, 0.7, center.z), color, radius * 1.3, 0.22);
    this.sparks.emit(new Vector3(center.x, 0.4, center.z), { color, count: 24, speed: 6, life: 0.45, up: 2.5, gravity: 10, size: 0.16, hot: true });
    this.smoke.emit(new Vector3(center.x, 0.3, center.z), { color: '#7a6a50', count: 10, speed: 2, life: 0.9, up: 0.8, spread: 0.4, size: 0.8, grow: 2.2, drag: 2.5 });
    this.shake = Math.max(this.shake, 0.12);
    const duration = 0.35;
    let t = 0;
    this.effects.push({
      update: (dt) => {
        t += dt;
        const k = t / duration;
        ring.scale.setScalar(radius * (0.3 + 0.7 * Math.min(1, k * 1.6)));
        mat.uniforms.progress!.value = 1.5;
        mat.uniforms.fade!.value = 1 - k;
        if (k >= 1) {
          ring.removeFromParent();
          mat.dispose();
          return false;
        }
        return true;
      },
    });
  }

  /** Kurz spod stóp w biegu. */
  footstep(at: Vector3, color: string): void {
    this.smoke.emit(at, { color, count: 3, speed: 0.6, life: 0.6, up: 0.4, size: 0.35, grow: 2.2, drag: 3, spread: 0.2 });
  }

  /** Trafienie: błysk i iskry w kolorze ataku, przy krytyku większe i złote. */
  impact(at: Vector3, color: string, crit: boolean): void {
    this.sparks.emit(at, { color: crit ? '#ffd860' : color, count: crit ? 14 : 7, speed: crit ? 6 : 3.5, life: 0.35, up: 1, gravity: 7, size: 0.18, grow: 0.3, hot: true });
    this.flash(at, crit ? '#ffe28a' : color, crit ? 1.8 : 1.1, 0.12);
  }

  /** Kurz i odpryski przy rozpadzie szkieleta. */
  death(at: Vector3): void {
    this.smoke.emit(at, { color: '#a8987a', count: 12, speed: 1.6, life: 0.9, up: 0.6, gravity: 0.4, spread: 0.6, size: 0.8, grow: 2.2, drag: 2 });
    this.sparks.emit(at, { color: '#e8dcc0', count: 6, speed: 3, life: 0.5, up: 2.5, gravity: 9, size: 0.14 });
  }

  update(dt: number): void {
    this.effects = this.effects.filter((e) => e.update(dt));
    this.shake = Math.max(0, this.shake - dt * 1.2);
  }
}

// ---------- pociski ----------

const orbCore = new SphereGeometry(0.13, 16, 12);
const boltShaft = new CylinderGeometry(0.03, 0.03, 0.9, 8).rotateZ(Math.PI / 2);
const boltTip = new ConeGeometry(0.075, 0.24, 8).rotateZ(-Math.PI / 2).translate(0.56, 0, 0);
const boltFletch = new BoxGeometry(0.16, 0.12, 0.01).translate(-0.4, 0, 0);
const knifeBlade = new ConeGeometry(0.06, 0.34, 4).rotateZ(-Math.PI / 2).scale(1, 0.35, 1).translate(0.1, 0, 0);
const knifeHandle = new CylinderGeometry(0.022, 0.022, 0.13, 6).rotateZ(Math.PI / 2).translate(-0.14, 0, 0);
const shardGeo = new OctahedronGeometry(0.2, 0).scale(2.4, 0.7, 0.7);
const shardSmall = new OctahedronGeometry(0.1, 0).scale(2, 0.7, 0.7);
const trailGeo = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(-0.5, 0, 0);
/** Odcinek błyskawicy: sześcian wydłużany wzdłuż osi Z (lookAt kieruje go na następny punkt). */
const lightningBox = new BoxGeometry(1, 1, 1);
const woodMat = new MeshStandardMaterial({ color: '#6b4a2b', roughness: 0.7 });
const steelMat = new MeshStandardMaterial({ color: '#e4e8ee', metalness: 0.9, roughness: 0.25 });
const featherMat = new MeshStandardMaterial({ color: '#c8402c', roughness: 0.9, side: DoubleSide });
const leatherMat = new MeshStandardMaterial({ color: '#3b2a1c', roughness: 0.9 });

export interface ProjectileView {
  root: Object3D;
  /** Część, która się kręci (nóż), albo null. */
  spinner: Object3D | null;
  /** Co klatkę: emisja smugi cząsteczek. */
  trail: { color: string; size: number; hot: boolean } | null;
  /** Elementy pulsujące (poświata kuli). */
  pulse: Sprite[];
}

function glowSprite(color: Color | string, size: number, opacity = 1): Sprite {
  const s = new Sprite(new SpriteMaterial({ map: glow(), color: new Color(color), blending: AdditiveBlending, depthWrite: false, opacity, toneMapped: false }));
  s.scale.setScalar(size);
  return s;
}

/** Płaska smuga za pociskiem (widoczna z kamery z góry). */
function trailPlane(color: Color | string, length: number, width: number): Mesh {
  const m = new Mesh(
    trailGeo,
    new MeshBasicMaterial({ map: streak(), color: new Color(color), transparent: true, blending: AdditiveBlending, depthWrite: false, toneMapped: false }),
  );
  m.scale.set(length, 1, width);
  return m;
}

/** Wygląd pocisku zależny od umiejętności: kula magii, bełt, nóż, lodowy odłamek. */
export function createProjectile(fx: string, color: string, scale: number): ProjectileView {
  const c = new Color(color);
  const root = new Group();

  if (fx === 'bolt') {
    root.add(new Mesh(boltShaft, woodMat), new Mesh(boltTip, steelMat));
    const f1 = new Mesh(boltFletch, featherMat);
    const f2 = f1.clone();
    f2.rotation.x = Math.PI / 2;
    root.add(f1, f2, trailPlane('#fff4d6', 1.6, 0.14));
    return { root, spinner: null, trail: null, pulse: [] };
  }

  if (fx === 'knife') {
    const knife = new Group();
    knife.add(new Mesh(knifeBlade, steelMat), new Mesh(knifeHandle, leatherMat));
    root.add(knife, glowSprite('#dfe8ff', 0.5, 0.35));
    return { root, spinner: knife, trail: { color: '#cfd8ea', size: 0.12, hot: false }, pulse: [] };
  }

  if (fx === 'shard') {
    const ice = new MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.9, transparent: true, opacity: 0.9, roughness: 0.05, metalness: 0.2 });
    root.add(new Mesh(shardGeo, ice));
    const s1 = new Mesh(shardSmall, ice);
    s1.position.set(-0.15, 0.08, 0.1);
    s1.rotation.set(0.4, 0.3, 0.2);
    const s2 = s1.clone();
    s2.position.set(-0.12, -0.06, -0.1);
    s2.rotation.set(-0.4, -0.2, -0.3);
    const halo = glowSprite(c, 1.3, 0.7);
    root.add(s1, s2, halo, trailPlane(c, 1.4, 0.35));
    return { root, spinner: null, trail: { color, size: 0.22, hot: false }, pulse: [halo] };
  }

  // orb (domyślnie): biały rdzeń, kolorowa poświata, smuga i wirujące iskry.
  root.add(new Mesh(orbCore, new MeshBasicMaterial({ color: new Color('#ffffff').lerp(c, 0.25), toneMapped: false })));
  const inner = glowSprite('#ffffff', 0.55 * scale, 0.9);
  const outer = glowSprite(c, 1.5 * scale, 0.9);
  root.add(inner, outer, trailPlane(c, 1.2, 0.4 * scale));
  return { root, spinner: null, trail: { color, size: 0.3 * scale, hot: true }, pulse: [outer] };
}
